import { useMutation, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { motion } from "framer-motion"
import {
	AppWindow,
	CalendarClock,
	ExternalLink,
	type LucideIcon,
	RefreshCw,
	Send,
	Smartphone,
} from "lucide-react"
import { type ReactNode, useState } from "react"
import { toast } from "sonner"
import {
	type AdminPost,
	PLATFORM_SURFACE,
	PLATFORM_TITLE,
	PlacementPill,
	type Platform,
	placementOf,
	setPostFreeAccess,
	setPostReviewShowcase,
	setPostSurface,
} from "@/entities/admin-post"
import { formatWhen } from "@/shared/lib/format-date"
import { fadeUp, staggerContainer } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { Progress } from "@/shared/ui/progress"
import { RowActionsMenu } from "@/shared/ui/row-actions-menu"
import { Switch } from "@/shared/ui/switch"
import { refreshPost, useChannelPublish } from "../model/use-channel-publish"
import { ScheduleDialog } from "./schedule-dialog"
import { LoadSwap } from "@/shared/ui/load-swap"

const ICON: Record<Platform, LucideIcon> = {
	telegram: Send,
	tma: AppWindow,
	mobile: Smartphone,
}

function PlatformCard({
	platform,
	status,
	details,
	actions,
	menu,
	footer,
	stateKey,
}: {
	platform: Platform
	/** Состояние площадки: при его смене текст, кнопки и прогресс сменяются переходом. */
	stateKey: string
	status: ReactNode
	details: ReactNode
	actions?: ReactNode
	menu?: ReactNode
	footer?: ReactNode
}) {
	const Icon = ICON[platform]
	return (
		<motion.article
			variants={fadeUp}
			className="rounded-2xl border border-border bg-card p-4 sm:p-5"
		>
			<div className="flex items-start gap-3.5">
				<span className="grid size-10 shrink-0 place-items-center rounded-xl bg-secondary text-foreground/80">
					<Icon className="size-[18px]" />
				</span>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
						<h3 className="font-semibold">{PLATFORM_TITLE[platform]}</h3>
						<LoadSwap loading={false} id={stateKey}>
							{status}
						</LoadSwap>
					</div>
					{/* Публикация меняет и текст, и кнопки: они сменяются перекрёстно, а высота
					    карточки тянется к новой, поэтому карточки ниже не прыгают. */}
					<LoadSwap loading={false} id={stateKey}>
						<div className="pt-1 text-sm leading-snug text-muted-foreground">{details}</div>
						{actions || menu ? (
							<div className="mt-4 flex flex-wrap items-center gap-2">
								{actions}
								{menu ? <div className="ml-auto">{menu}</div> : null}
							</div>
						) : null}
						{footer}
					</LoadSwap>
				</div>
			</div>
		</motion.article>
	)
}

function ChannelCard({ post }: { post: AdminPost }) {
	const { confirm } = useConfirm()
	const [scheduling, setScheduling] = useState(false)
	const channel = useChannelPublish(post)
	const placement = placementOf(post, "telegram")
	const { publication, job, busy } = channel

	const details = job ? (
		"Отправляем в канал. Страницу можно не держать открытой."
	) : placement.state === "live" ? (
		<>
			{placement.at ? `Вышел ${formatWhen(placement.at)}. ` : null}
			{post.postUrl ? (
				<a
					href={post.postUrl}
					target="_blank"
					rel="noreferrer"
					className="inline-flex items-center gap-1 text-foreground hover:[--link-underline:100%] focus-visible:[--link-underline:100%]"
				>
					<span className="link-underline">Открыть в Telegram</span>
					<ExternalLink className="size-3.5" />
				</a>
			) : null}
		</>
	) : placement.state === "scheduled" && placement.at ? (
		`Уйдёт в канал ${formatWhen(placement.at)}.`
	) : placement.state === "failed" ? (
		<span className="text-red-300">
			{placement.error || "Telegram не принял сообщение."} Проверьте текст и файлы и повторите.
		</span>
	) : (
		"Пост уйдёт сообщением в канал клуба, с файлами альбомом."
	)

	const actions =
		placement.state === "live" ? (
			<Button
				size="sm"
				variant="secondary"
				disabled={busy}
				isLoading={channel.sync.isPending}
				onClick={() => channel.sync.mutate()}
			>
				<RefreshCw />
				Обновить сообщение
			</Button>
		) : placement.state === "scheduled" ? (
			<>
				<Button size="sm" variant="secondary" disabled={busy} onClick={() => setScheduling(true)}>
					<CalendarClock />
					Перенести
				</Button>
				<Button
					size="sm"
					disabled={busy}
					isLoading={channel.publish.isPending}
					onClick={() =>
						confirm({
							title: "Опубликовать сейчас?",
							message: "Пост уйдёт в канал сразу, а не в запланированное время.",
							confirmText: "Опубликовать",
							onConfirm: () => channel.publish.mutateAsync(undefined).then(() => undefined),
						})
					}
				>
					<Send />
					Опубликовать сейчас
				</Button>
			</>
		) : placement.state === "failed" ? (
			<Button
				size="sm"
				disabled={busy}
				isLoading={channel.retry.isPending}
				onClick={() => channel.retry.mutate()}
			>
				<RefreshCw />
				Повторить
			</Button>
		) : (
			<>
				<Button
					size="sm"
					disabled={busy}
					isLoading={channel.publish.isPending}
					onClick={() =>
						confirm({
							title: "Опубликовать в канал?",
							message: "Сообщение сразу увидят подписчики канала.",
							confirmText: "Опубликовать",
							onConfirm: () => channel.publish.mutateAsync(undefined).then(() => undefined),
						})
					}
				>
					<Send />
					Опубликовать
				</Button>
				<Button size="sm" variant="secondary" disabled={busy} onClick={() => setScheduling(true)}>
					<CalendarClock />
					Запланировать
				</Button>
			</>
		)

	const menu =
		publication && placement.state !== "off" ? (
			<RowActionsMenu
				label="Ещё действия с каналом"
				actions={[
					{
						label: "Отправить заново",
						hidden: placement.state !== "live",
						onSelect: () =>
							confirm({
								title: "Отправить пост заново?",
								message:
									"Старое сообщение удалится, новое придёт подписчикам как свежий пост. Нужно, когда правкой альбом не обновить.",
								confirmText: "Отправить заново",
								onConfirm: () => channel.republish.mutateAsync().then(() => undefined),
							}),
					},
					{
						label: "Отменить публикацию",
						hidden: placement.state !== "scheduled",
						destructive: true,
						onSelect: () =>
							confirm({
								title: "Отменить публикацию?",
								message: "Пост не уйдёт в канал. Запланировать можно будет снова.",
								confirmText: "Отменить публикацию",
								onConfirm: () => channel.cancel.mutateAsync().then(() => undefined),
							}),
					},
					{
						label: "Удалить из канала",
						hidden: placement.state !== "live",
						destructive: true,
						separatorBefore: true,
						onSelect: () =>
							confirm({
								title: "Удалить сообщение из канала?",
								message:
									"Подписчики его больше не увидят. В приложениях материал останется, если он там опубликован.",
								confirmText: "Удалить из канала",
								onConfirm: () => channel.unpublish.mutateAsync().then(() => undefined),
							}),
					},
				]}
			/>
		) : null

	return (
		<>
			<PlatformCard
				platform="telegram"
				stateKey={job ? "job" : placement.state}
				status={
					<PlacementPill
						state={job ? "scheduled" : placement.state}
						label={job ? "Отправляется" : undefined}
					/>
				}
				details={details}
				actions={job ? null : actions}
				menu={job ? null : menu}
				footer={
					job ? (
						<div className="mt-4 space-y-1.5">
							<Progress value={job.progress} />
							<p className="text-xs text-muted-foreground">{job.message}</p>
						</div>
					) : null
				}
			/>
			<ScheduleDialog
				open={scheduling}
				onOpenChange={setScheduling}
				initial={placement.state === "scheduled" ? placement.at : null}
				pending={channel.publish.isPending}
				onSubmit={(date) => channel.publish.mutate(date, { onSuccess: () => setScheduling(false) })}
			/>
		</>
	)
}

/** Галочка открытого поста: его видят и те, кто не состоит в канале клуба. */
function FreeAccessToggle({ post }: { post: AdminPost }) {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationFn: (enabled: boolean) => setPostFreeAccess(post.id, enabled),
		onSuccess: async ({ freeAccess, total }) => {
			toast.success(
				freeAccess
					? `Открыто для всех, таких постов ${total}`
					: `Снова только для клуба, открытых постов ${total}`,
			)
			await refreshPost(queryClient, post.id)
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось изменить доступ")),
	})

	return (
		<label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-border px-3 py-2.5">
			<Switch
				className="mt-0.5"
				checked={post.freeAccess ?? false}
				disabled={mutation.isPending}
				onCheckedChange={(enabled) => mutation.mutate(enabled)}
			/>
			<span className="min-w-0 text-sm">
				<span className="block font-medium">Открыт всем</span>
				<span className="block text-muted-foreground">
					Пост видят и те, кто не состоит в канале клуба. Остальной каталог для них закрыт.
				</span>
			</span>
		</label>
	)
}

/** Галочка подборки для ревью: пока App Store проверяет iOS, лента состоит только из отмеченных постов. */
function ReviewShowcaseToggle({ post }: { post: AdminPost }) {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationFn: (enabled: boolean) => setPostReviewShowcase(post.id, enabled),
		onSuccess: async ({ reviewShowcase, total }) => {
			toast.success(
				reviewShowcase
					? `Добавлено в подборку для ревью, в ней ${total}`
					: `Убрано из подборки для ревью, в ней ${total}`,
			)
			await refreshPost(queryClient, post.id)
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось изменить подборку")),
	})

	return (
		<label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-border px-3 py-2.5">
			<Switch
				className="mt-0.5"
				checked={post.reviewShowcase ?? false}
				disabled={mutation.isPending}
				onCheckedChange={(enabled) => mutation.mutate(enabled)}
			/>
			<span className="min-w-0 text-sm">
				<span className="block font-medium">Показывать ревьюерам App Store</span>
				<span className="block text-muted-foreground">
					Пока идёт ревью, лента в iOS состоит только из отмеченных постов. Хватит около 10.
				</span>
			</span>
		</label>
	)
}

const SURFACE_AUDIENCE: Record<"tma" | "mobile", string> = {
	tma: "пользователи мини-аппа в Telegram",
	mobile: "пользователи приложения на iOS и Android",
}

function SurfaceCard({ post, platform }: { post: AdminPost; platform: "tma" | "mobile" }) {
	const { confirm } = useConfirm()
	const queryClient = useQueryClient()
	const placement = placementOf(post, platform)
	const live = placement.state === "live"
	const hasCover = post.media?.some((item) => item.type === "preview")
	const hasFormat = post.formats?.length > 0

	const mutation = useMutation({
		mutationFn: (status: "VISIBLE" | "HIDDEN") =>
			setPostSurface(post.id, PLATFORM_SURFACE[platform], status),
		onSuccess: async (_, status) => {
			toast.success(
				status === "VISIBLE"
					? `Опубликовано: ${PLATFORM_TITLE[platform].toLowerCase()}`
					: `Снято: ${PLATFORM_TITLE[platform].toLowerCase()}`,
			)
			await refreshPost(queryClient, post.id)
		},
		onError: (error) =>
			toast.error(extractApiErrorMessage(error, "Не удалось изменить публикацию")),
	})

	const warnings = [
		!hasCover && "без обложки в каталоге будет заглушка",
		!hasFormat && "без формата материал не попадёт ни в один фильтр",
	].filter(Boolean) as string[]

	return (
		<PlatformCard
			platform={platform}
			stateKey={placement.state}
			status={<PlacementPill state={placement.state} />}
			footer={
				live ? (
					<>
						<FreeAccessToggle post={post} />
						{platform === "mobile" ? <ReviewShowcaseToggle post={post} /> : null}
					</>
				) : null
			}
			details={
				live ? (
					<>
						Материал видят {SURFACE_AUDIENCE[platform]}
						{placement.at ? `. С ${formatWhen(placement.at)}` : ""}.
					</>
				) : (
					<>
						После публикации материал увидят {SURFACE_AUDIENCE[platform]}.
						{warnings.length ? (
							<span className="mt-1 block text-amber-300/90">
								Стоит поправить: {warnings.join(", ")}.
							</span>
						) : null}
					</>
				)
			}
			actions={
				live ? (
					<Button
						size="sm"
						variant="secondary"
						isLoading={mutation.isPending}
						onClick={() =>
							confirm({
								title: "Снять материал?",
								message: `Его перестанут видеть ${SURFACE_AUDIENCE[platform]}. Избранное и история у них сохранятся.`,
								confirmText: "Снять",
								onConfirm: () => mutation.mutateAsync("HIDDEN").then(() => undefined),
							})
						}
					>
						Снять с публикации
					</Button>
				) : (
					<Button
						size="sm"
						isLoading={mutation.isPending}
						onClick={() => mutation.mutate("VISIBLE")}
					>
						<Send />
						Опубликовать
					</Button>
				)
			}
		/>
	)
}

/** Три площадки материала: у каждой своё состояние и свои действия. */
export function PublicationPanel({ post, className }: { post: AdminPost; className?: string }) {
	return (
		<motion.div
			variants={staggerContainer(0.05)}
			initial="hidden"
			animate="visible"
			className={cn("space-y-3", className)}
		>
			<ChannelCard post={post} />
			<SurfaceCard post={post} platform="tma" />
			<SurfaceCard post={post} platform="mobile" />
		</motion.div>
	)
}
